import { useCallback, useEffect } from "react";

import { QuestionnaireItem, QuestionnaireResponseItem } from "@medplum/fhirtypes";

import { Box, Button, Divider, Group } from "@mantine/core";
import { DateInput, TimeInput } from "@mantine/dates";

import { IconCalendar, IconClock } from "@tabler/icons-react";
import { format, getHours, getMinutes, getSeconds, parseISO, set } from "date-fns";

import { TextWrapper } from "@/components/TextWrapper";
import { Extensions } from "@/fhir/enums/extensions";
import { useStableCalendarDateInputState } from "@/intake-forms/hooks/useStableCalendarDateInputState";
import { IntakeFormComponentProps } from "@/intake-forms/types/intake-forms";
import { setFormFieldValue } from "@/intake-forms/utils/intake-forms";
import { questionnaireFieldTestId } from "@/intake-forms/utils/questionnaire-field-testid";
import { useControlledTimeInput } from "@/utils/hooks/useControlledTimeInput";

/**
 * Extracts and merges date/time values from questionnaire response items
 *
 * @param questionnaireResponseItem - The questionnaire response item containing date/time data
 * @returns The merged date/time value as an ISO string, or null if no valid date/time is found
 */
function extractDateTimeValue(
  questionnaireResponseItem?: QuestionnaireResponseItem,
): string | null {
  if (!questionnaireResponseItem) return null;

  // Get initial date and time from questionnaire response if available
  const dateItem = questionnaireResponseItem.item?.find((item) => item.linkId.endsWith("Date"));
  const timeItem = questionnaireResponseItem.item?.find((item) => item.linkId.endsWith("Time"));
  const dateTimeItem = questionnaireResponseItem.item?.find(
    (item) =>
      !item.linkId.endsWith("Date") &&
      !item.linkId.endsWith("Time") &&
      item.answer?.[0]?.valueDateTime,
  );

  // Return the merged date/time value if we have both date and time
  if (dateItem?.answer?.[0]?.valueDate && timeItem?.answer?.[0]?.valueTime) {
    const date = dateItem.answer[0].valueDate;
    const time = timeItem.answer[0].valueTime;
    const dateTime = new Date(`${date}T${time}`);
    return dateTime.toISOString();
  }

  // Return the dateTime value if available
  if (dateTimeItem?.answer?.[0]?.valueDateTime) {
    return dateTimeItem.answer[0].valueDateTime;
  }

  return null;
}

/**
 * Returns midnight on the local calendar date for the given reference instant.
 */
function getLocalCalendarDateAnchor(reference = new Date()): Date {
  return new Date(reference.getFullYear(), reference.getMonth(), reference.getDate());
}

/**
 * Merges a local HH:mm:ss time into an ISO datetime.
 * When no date is set yet, anchors to today's local calendar date.
 */
function mergeLocalTimeIntoIso(isoValue: string | null | undefined, display: string): string {
  const dateSource = isoValue ? new Date(isoValue) : getLocalCalendarDateAnchor();
  const [hours, minutes, seconds] = display.split(":").map(Number);
  dateSource.setHours(hours, minutes, seconds, 0);
  return dateSource.toISOString();
}

export interface QuestionnaireDateTimeFieldProps<T>
  extends Omit<IntakeFormComponentProps<T>, "key"> {
  error: string | undefined | React.ReactNode;
  questionnaireResponseItem?: QuestionnaireResponseItem;
  questionnaireItem?: QuestionnaireItem;
}

export default function QuestionnaireDateTimeField<T>({
  form,
  formFieldName,
  label,
  required,
  error,
  questionnaireResponseItem,
  questionnaireItem,
}: QuestionnaireDateTimeFieldProps<T>) {
  const formInputProps = form.getInputProps(formFieldName);
  const isoValue = formInputProps.value as string | null | undefined;

  useEffect(() => {
    const dateTimeValue = extractDateTimeValue(questionnaireResponseItem);
    if (dateTimeValue) {
      setFormFieldValue(form, formFieldName, dateTimeValue);
    }
  }, [questionnaireResponseItem, form, formFieldName]);

  const handleNowClick = () => {
    const now = new Date();
    setFormFieldValue(form, formFieldName, now.toISOString());
  };

  const storedToDisplay = useCallback((iso: string) => format(new Date(iso), "HH:mm:ss"), []);

  const displayToStored = useCallback(
    (display: string) => mergeLocalTimeIntoIso(isoValue, display),
    [isoValue],
  );

  const onCommit = (value: string) => {
    setFormFieldValue(form, formFieldName, value);
  };

  const { displayValue, inputRef, onFocus, onBlur, onChange, openPicker } = useControlledTimeInput({
    storedValue: isoValue,
    onCommit,
    storedToDisplay,
    displayToStored,
    autoPopulate: false,
    resetToCurrentTimeOnBlur: false,
  });

  const { stableDateValue, calendarDate, handleCalendarDateChange } =
    useStableCalendarDateInputState(formInputProps.value);

  const handleDateChange = (dateVal: string | null) => {
    if (!dateVal) {
      setFormFieldValue(form, formFieldName, null);
      return;
    }

    // Mantine v8 returns "YYYY-MM-DD" strings; parseISO treats them as local calendar
    // dates instead of UTC midnight from `new Date("YYYY-MM-DD")`.
    const selectedCalendarDate = parseISO(dateVal);
    const currentDateTime = formInputProps.value
      ? parseISO(formInputProps.value as string)
      : new Date();

    const correctedDate = set(selectedCalendarDate, {
      hours: getHours(currentDateTime),
      minutes: getMinutes(currentDateTime),
      seconds: getSeconds(currentDateTime),
      milliseconds: 0,
    });

    setFormFieldValue(form, formFieldName, correctedDate.toISOString());
  };

  // Check if this field should use normal label styling instead of dimmed
  // Look for the extension in the questionnaire item
  const useNormalLabelStyle = questionnaireItem?.extension?.some(
    (ext) => ext.url === Extensions.QuestionnaireFieldLabelStyle && ext.valueString === "normal",
  );

  const fieldTestId = questionnaireFieldTestId(formFieldName);

  return (
    <>
      <Divider my="md" />
      {label && (
        <TextWrapper variant="label" c={useNormalLabelStyle ? undefined : "dimmed"}>
          {label}
        </TextWrapper>
      )}

      <Group>
        <DateInput
          w={175}
          clearable
          label="Date"
          error={error}
          date={calendarDate}
          allowDeselect={false}
          value={stableDateValue}
          withAsterisk={required}
          valueFormat="MM/DD/YYYY"
          placeholder="MM/DD/YYYY"
          onChange={handleDateChange}
          onDateChange={handleCalendarDateChange}
          leftSection={<IconCalendar size={20} />}
          data-testid={fieldTestId ? `${fieldTestId}-date` : undefined}
        />
        <TimeInput
          withSeconds
          label="Time"
          error={error}
          ref={inputRef}
          onBlur={onBlur}
          onFocus={onFocus}
          onChange={onChange}
          value={displayValue}
          withAsterisk={required}
          data-testid={fieldTestId ? `${fieldTestId}-time` : undefined}
          leftSection={
            <Box
              component="span"
              style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
              onClick={(event) => {
                event.stopPropagation();
                openPicker();
              }}
            >
              <IconClock size={20} stroke={1.5} />
            </Box>
          }
        />
        <Button mt="lg" size="sm" variant="outline" color="burgundy.9" onClick={handleNowClick}>
          Now
        </Button>
      </Group>
    </>
  );
}
